import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { X, Circle, Coins, TrendingUp } from 'lucide-react'
import { Card, CardContent } from './ui/card'
import { Badge } from './ui/badge'
import { useLanguage } from '../context/LanguageContext'
import { cn } from '../lib/utils'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function InvestmentModal({ isOpen, onClose, month, onTransactionUpdated }) {
  const { locale, formatCurrency } = useLanguage()
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  
  const dateLocale = locale === 'es-MX' ? es : enUS
  const isSpanish = locale === 'es-MX'

  const fetchInvestments = useCallback(async () => {
    if (!month) return
    setLoading(true)
    try {
      // Parse month to get start and end dates
      const [year, monthNum] = month.split('-').map(Number)
      const monthDate = new Date(year, monthNum - 1, 1)
      const start = format(startOfMonth(monthDate), 'yyyy-MM-dd')
      const end = format(endOfMonth(monthDate), 'yyyy-MM-dd')
      
      // Fetch all transactions for the month
      const response = await axios.get(`${API}/finance/transactions`, {
        params: {
          start_date: start,
          end_date: end,
          limit: 200
        }
      })
      
      // Filter only investments (es_inversion = true) and sort by amount descending
      const investments = response.data
        .filter(tx => tx.es_inversion === true)
        .sort((a, b) => b.monto - a.monto)
      setTransactions(investments)
    } catch (error) {
      console.error('Error fetching investments:', error)
    } finally {
      setLoading(false)
    }
  }, [month])

  useEffect(() => {
    if (isOpen) {
      fetchInvestments()
      // Animate in
      requestAnimationFrame(() => setIsVisible(true))
    } else {
      setIsVisible(false)
    }
  }, [isOpen, fetchInvestments])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 200)
  }

  // Calculate total
  const totalInvestments = transactions.reduce((sum, tx) => sum + tx.monto, 0)

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className={cn(
          "fixed inset-0 bg-black/60 z-50 transition-opacity duration-200",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className={cn(
          "fixed left-0 right-0 bottom-0 z-50 bg-background rounded-t-3xl transition-transform duration-200 ease-out shadow-2xl",
          isVisible ? "translate-y-0" : "translate-y-full"
        )}
        style={{ 
          maxHeight: '80vh',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)'
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-4 pb-2">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-4 border-b">
          <div className="flex items-center gap-2">
            <Coins className="w-6 h-6 text-emerald-500" />
            <div>
              <h2 className="text-lg font-bold">
                {isSpanish ? 'Inversiones del Mes' : 'Monthly Investments'}
              </h2>
              <p className="text-xs text-muted-foreground">
                {format(new Date(month + '-01'), 'MMMM yyyy', { locale: dateLocale })}
              </p>
            </div>
          </div>
          <button 
            onClick={handleClose} 
            className="p-2 -mr-2 rounded-full hover:bg-secondary"
            data-testid="close-investment-modal-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary */}
        <div className="px-4 py-3 bg-emerald-500/10 border-b border-emerald-500/20">
          <div className="flex items-center justify-between">
            <span className="text-sm text-emerald-700 dark:text-emerald-400">
              {isSpanish ? 'Total invertido:' : 'Total invested:'}
            </span>
            <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
              {formatCurrency(totalInvestments)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {transactions.length} {isSpanish ? 'transacciones' : 'transactions'}
          </p>
        </div>

        {/* Transactions List */}
        <div className="overflow-y-auto px-4 py-4" style={{ maxHeight: 'calc(80vh - 180px)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-pulse text-muted-foreground">
                {isSpanish ? 'Cargando...' : 'Loading...'}
              </div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {isSpanish ? 'No hay inversiones este mes' : 'No investments this month'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {isSpanish ? '¡Empieza a invertir!' : 'Start investing!'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <Card 
                  key={tx.id} 
                  className="overflow-hidden"
                  data-testid={`investment-tx-${tx.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${tx.categoria_color || '#10b981'}20` }}
                      >
                        <Circle 
                          className="w-5 h-5" 
                          style={{ color: tx.categoria_color || '#10b981' }} 
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{tx.categoria_nombre}</p>
                          <Badge 
                            variant="outline" 
                            className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                          >
                            <TrendingUp className="w-3 h-3 mr-1" />
                            {isSpanish ? 'Inversión' : 'Investment'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{format(parseISO(tx.fecha), 'd MMM', { locale: dateLocale })}</span>
                          <span>•</span>
                          <span>{tx.metodo}</span>
                          {tx.notas && (
                            <>
                              <span>•</span>
                              <span className="truncate">{tx.notas}</span>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(tx.monto, tx.moneda)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-4 py-3 border-t">
          <p className="text-xs text-center text-muted-foreground">
            {isSpanish 
              ? 'Para editar, ve a la lista principal de finanzas' 
              : 'To edit, go to the main finance list'}
          </p>
        </div>
      </div>
    </>
  )
}
